import {
  FontColorEnum,
  FontSizeEnum,
  FontWeightEnum,
  LoaderIcon,
  Tooltip,
  Typography,
  WarningInfoIcon,
} from '@acceso/design-system';
import { Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { v4 as uuiv4 } from 'uuid';

import { useCatalogueSelectProps } from '@/components/CatalogueListbox/use-catalogue-select-props';
import { CollapseAllButton } from '@/components/CollapseAllButton/CollapseAllButton';
import { SelectForm } from '@/components/FormFields/SelectForm';
import { PresenceGroupCard } from '@/components/PresenceGroupCard/PresenceGroupCard';
import { ThumbnailSizeSlider } from '@/components/ThumbnailSizeSlider/ThumbnailSizeSlider';
import { EPSILON } from '@/constants';
import { TSegmentPeopleClip } from '@/features/broadcast-schedule-program/components/BroadcastScheduleProgramBody/components/BroadcastScheduleProgramBodySegmentToolkit/components/BroadcastScheduleProgramBodySegmentToolkitCuts/types';
import { useBroadcastScheduleProgram } from '@/features/broadcast-schedule-program/context/BroadcastScheduleProgramContext';
import { findSegmentByDate } from '@/features/broadcast-schedule-program/helpers/find-segment-by-date';
import { isGroupOutsideCut } from '@/features/broadcast-schedule-program/helpers/is-group-outside-cut';
import { usePatchClipPeople } from '@/features/clips/api/patch-clips-people';
import { FeaturedPeopleDrawerForm } from '@/features/featured-people/components/FeaturedPeopleDrawerForm';
import { usePeoplePicker } from '@/features/featured-people/hooks/use-people-picker';
import { useGetSegmentId } from '@/features/segments/api/get-segment-id';
import { useAddPresencesGrouping } from '@/hooks/use-add-presences-grouping';
import { latestImageOf } from '@/hooks/use-catalogue-picker';
import { useItemThumbnailMap } from '@/hooks/use-item-thumbnail-map';
import { VIDEO_OFFSET_TIME_CONTROL } from '@/hooks/use-presence-preview-box';
import { useNotificationStore } from '@/stores/notifications';
import { PresenceTypeEnum } from '@/types/presences';
import { convertDatesToSeconds, getAbsoluteDateFromPlayerTime } from '@/utils/hh-mm-ss-into-number';
import { resolveItemId } from '@/utils/presence-groups';

import { useBroadcastScheduleProgramBodySegmentToolkitPeopleFormState } from '../BroadcastScheduleProgramBodySegmentToolkit/components/BroadcastScheduleProgramBodySegmentToolkitPeople/hooks/useBroadcastScheduleProgramBodySegmentToolkitPeopleFormState';
import { ClipTooltip } from '../ClipTooltip/ClipTooltip';

type AddPeopleProps = {
  people: TSegmentPeopleClip[];
  setPeople: Dispatch<SetStateAction<TSegmentPeopleClip[]>>;
  disabled?: boolean;
  isLoadingPreview?: boolean;
  showWarningPresences?: boolean;
  cutStart?: Date | null;
  cutEnd?: Date | null;
};

export function AddPeople({
  people,
  disabled,
  setPeople,
  isLoadingPreview,
  showWarningPresences = false,
  cutStart = null,
  cutEnd = null,
}: AddPeopleProps) {
  const {
    activeClip,
    drawerRect,
    drawingFor,
    visibleBox,
    activeSegment,
    drawerLogo,
    playerVideoCurrentTime,
    segmentItems,
    setDrawingFor,
    setVisibleBox,
    setDrawerLogo,
    setDrawerRect,
    setActiveSegment,
    setPendingSeekTime,
    videoControlRef,
  } = useBroadcastScheduleProgram();
  const {
    isCreatingPeople,
    form,
    peopleDrawer: drawerPeopleForm,
    handleSubmitPeople,
    renderAdditionalButtons,
  } = useBroadcastScheduleProgramBodySegmentToolkitPeopleFormState();
  const peoplePicker = usePeoplePicker({ value: form.watch('people_id') });
  const peopleSelectProps = useCatalogueSelectProps(peoplePicker);
  const { findOption } = peoplePicker;
  const { t } = useTranslation();
  const itemThumbnailMap = useItemThumbnailMap(people, segmentItems);

  const [segmentIdToFetch, setSegmentIdToFetch] = useState<string | null>(null);
  const { data: fetchedSegment } = useGetSegmentId({ id: segmentIdToFetch || '' });
  const { addWarningNotification } = useNotificationStore();

  useEffect(() => {
    if (fetchedSegment) {
      setActiveSegment(fetchedSegment);
      setSegmentIdToFetch(null);
    }
  }, [fetchedSegment]);

  const {
    groupedItems,
    selectedIds,
    setSelectedIds,
    thumbnailSize,
    setThumbnailSize,
    toggleGroupSelection,
    toggleItemSelection,
  } = useAddPresencesGrouping(people, 'people_id');

  const [allCollapsed, setAllCollapsed] = useState(true);

  const patchClipPeople = usePatchClipPeople();

  const handleReviewGroup = useCallback(
    (leafIds: string[], reviewState: 0 | 1) => {
      const items = people.filter(p => leafIds.includes(resolveItemId(p)) && p.id);
      if (!items.length) return;

      items.forEach((item, i) => {
        patchClipPeople.mutate({
          id: item.id!,
          review_state: reviewState,
          silently: i !== items.length - 1,
        });
      });

      setPeople(prev =>
        prev.map(p =>
          leafIds.includes(resolveItemId(p)) ? { ...p, review_state: reviewState } : p
        )
      );
    },
    [people, patchClipPeople, setPeople]
  );

  const handleValidateGroup = useCallback(
    (leafIds: string[]) => handleReviewGroup(leafIds, 1),
    [handleReviewGroup]
  );
  const handleInvalidateGroup = useCallback(
    (leafIds: string[]) => handleReviewGroup(leafIds, 0),
    [handleReviewGroup]
  );

  const handlePreview = useCallback(
    (box: TSegmentPeopleClip) => {
      if (!box.parameters) throw new Error('box.parameters is undefined');
      if (!segmentItems) throw new Error('segmentItems is undefined');

      const startDate = box.s_datetime || box.start_datetime;
      const segmentToUpdate = findSegmentByDate(segmentItems, startDate);

      let seconds = 0;
      if (segmentToUpdate) {
        setActiveSegment(segmentToUpdate);
        seconds =
          convertDatesToSeconds(startDate, segmentToUpdate.start_datetime) +
          VIDEO_OFFSET_TIME_CONTROL;
      } else {
        addWarningNotification(
          'No se encontró el segmento para la fecha seleccionada.',
          'Error al buscar segmento'
        );
        return;
      }
      setPendingSeekTime?.(seconds);
      videoControlRef.current?.seekToSecond(seconds);

      setVisibleBox(prevVisibleBox => {
        if (
          prevVisibleBox &&
          prevVisibleBox.bbox.every(
            (val, index) => Math.abs(val - box.parameters?.bbox[index]) < EPSILON
          )
        ) {
          return null;
        }
        return {
          bbox: box.parameters?.bbox,
          entity_label: box.entity_label || '',
          frame: seconds || 0,
          entity_score: box.entity_score || 0,
          id: box.tempId || box?.id,
        };
      });
    },
    [segmentItems, videoControlRef]
  );

  const handleClickAddPeople = useCallback(() => {
    if (drawerLogo && drawingFor === 'people') {
      setDrawerLogo(false);
      setDrawingFor(null);
      setDrawerRect(null);
      return;
    }
    if (drawerLogo) {
      setDrawingFor('people');
      return;
    } else {
      setDrawerLogo(true);
      setDrawingFor('people');
    }
  }, [drawingFor, drawerLogo]);

  const handleChangeSelect = useCallback(
    (value: any) => {
      if (!activeSegment) throw new Error('activeSegment is undefined');

      const newDateTime = getAbsoluteDateFromPlayerTime(
        activeSegment.start_datetime,
        playerVideoCurrentTime
      );

      const chosen = findOption(value);
      const newPeople: any = {
        people_id: chosen?.option.value,
        tempId: uuiv4(),
        entity_label: chosen?.option.label,
        s_datetime: newDateTime,
        e_datetime: newDateTime,
        image: latestImageOf(chosen),
        addedManually: true,
        parameters: {
          bbox: drawerRect || [0, 0, 0, 0],
          segment_id: activeSegment?.id || '',
        },
        entity_score: 1,
      };
      setPeople(prev => [...prev, newPeople]);
      setDrawerLogo(false);
      setDrawerRect(null);
    },
    [setPeople, setDrawerLogo, drawerRect, findOption, activeSegment, playerVideoCurrentTime]
  );

  const handleBatchDelete = useCallback(
    (leafIds: string[]) => {
      setPeople(prev => prev.filter(p => !leafIds.includes(resolveItemId(p))));
      setSelectedIds(prev => prev.filter(id => !leafIds.includes(id)));
      setVisibleBox(prev => (prev && leafIds.includes(String(prev.id ?? '')) ? null : prev));
    },
    [setPeople, setSelectedIds, setVisibleBox]
  );

  const handleBatchMove = useCallback(
    (targetPeopleId: string, leafIds: string[]) => {
      const target = findOption(targetPeopleId);
      setPeople(prev =>
        prev.map(p =>
          leafIds.includes(resolveItemId(p))
            ? {
                ...p,
                people_id: targetPeopleId,
                entity_label: target?.option.label ?? p.entity_label,
                image: latestImageOf(target) || p.image,
              }
            : p
        )
      );
      setSelectedIds(prev => prev.filter(id => !leafIds.includes(id)));
    },
    [setPeople, setSelectedIds, findOption]
  );

  // Refreshing data when clip change
  useEffect(() => {
    form.resetField('people_id');
  }, [activeClip?.id]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.ctrlKey && event.key.toLocaleLowerCase() === 'p') {
        event.preventDefault();
        handleClickAddPeople();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleClickAddPeople]);

  return (
    <div className="flex flex-col gap-2" data-id="add-people-container">
      <div className="flex justify-between gap-2">
        <div className="flex items-center gap-1">
          <Typography
            fontSize={FontSizeEnum.Lg}
            weight={FontWeightEnum.Bold}
            color={FontColorEnum.Medium20}
          >
            {t('Personas de interés')}:
          </Typography>
          <Typography
            fontSize={FontSizeEnum.Lg}
            color={FontColorEnum.Medium20}
            weight={FontWeightEnum.Semibold}
          >
            {people.length}
          </Typography>
          {showWarningPresences && (
            <Tooltip help={t('Una o varias personas no entran dentro del tiempo del corte')}>
              <WarningInfoIcon className="text-red-500" />
            </Tooltip>
          )}
        </div>
        <ClipTooltip handleClick={handleClickAddPeople} disabled={disabled} />
      </div>

      {drawerRect && drawingFor === 'people' && (
        <div className="mb-5 overflow-hidden rounded-lg border-[8px] border-gray-200">
          <SelectForm
            form={form as any}
            name="people_id"
            label={t('Persona de interés')}
            hideLabelTag={true}
            {...peopleSelectProps}
            handleChange={handleChangeSelect}
            additionalButtons={renderAdditionalButtons()}
          />
        </div>
      )}

      {isLoadingPreview ? (
        <div className="flex w-full justify-center">
          <LoaderIcon className="animate-spin" />
        </div>
      ) : (
        groupedItems.length > 0 && (
          <div className="flex flex-col overflow-x-auto">
            <div className="flex items-center justify-end gap-6 border-t border-dotted border-gray-200 px-1 pt-4">
              <div className="min-w-0">
                <ThumbnailSizeSlider value={thumbnailSize} onChange={setThumbnailSize} />
              </div>
              <CollapseAllButton
                isExpanded={!allCollapsed}
                onClick={() => setAllCollapsed(prev => !prev)}
              />
            </div>
            <div className="flex flex-col gap-3 py-2" data-id="people-list-container">
              {groupedItems.map((group, i) => {
                const key = (group as any).children
                  ? (group as any).id
                  : ((group as any).people_id ?? resolveItemId(group as any)) || String(i);
                const groupIsOutsideCut = isGroupOutsideCut<TSegmentPeopleClip>(
                  group as any,
                  cutStart,
                  cutEnd
                );

                return (
                  <PresenceGroupCard
                    key={key}
                    group={group as any}
                    thumbnailSize={thumbnailSize}
                    selectedIds={selectedIds}
                    activeItemId={visibleBox?.id?.toString()}
                    onToggleGroupSelection={toggleGroupSelection}
                    onToggleItemSelection={toggleItemSelection}
                    onPreview={handlePreview}
                    onValidateGroup={handleValidateGroup}
                    onInvalidateGroup={handleInvalidateGroup}
                    onMove={handleBatchMove}
                    onDelete={handleBatchDelete}
                    itemThumbnailMap={itemThumbnailMap}
                    presenceType={PresenceTypeEnum.PEOPLE}
                    isCollapsed={allCollapsed}
                    isOutsideCut={groupIsOutsideCut}
                  />
                );
              })}
            </div>
          </div>
        )
      )}

      <FeaturedPeopleDrawerForm
        isOpen={drawerPeopleForm.isOpen}
        isMutating={isCreatingPeople}
        onClose={drawerPeopleForm.close}
        onSubmit={handleSubmitPeople}
      />
    </div>
  );
}
